import rawConfig from '../data/config.json';
import type { SimState } from '../sim/types';
import { migrateEnvelope } from './migration';
import type { Clock, LoadResult, SaveEnvelope, SaveResult, SlotId, StorageLike } from './types';
import { CURRENT_SCHEMA_VERSION } from './types';
import { validateEnvelope, validateState } from './validate';

export function getSlotKey(slot: SlotId): string {
  const prefix = rawConfig.storage.slotPrefix || 'project-hospital:';
  return `${prefix}${slot}`;
}

export function saveSlot(
  storage: StorageLike,
  slot: SlotId,
  state: SimState,
  clock: Clock = () => new Date().toISOString()
): SaveResult {
  try {
    const envelope: SaveEnvelope = {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      savedAt: clock(),
      state: JSON.parse(JSON.stringify(state)) as SimState
    };

    const serialized = JSON.stringify(envelope);
    storage.setItem(getSlotKey(slot), serialized);
    return { ok: true, savedAt: envelope.savedAt };
  } catch (err: unknown) {
    if (err instanceof Error) {
      if (err.name === 'QuotaExceededError' || err.message.includes('quota')) {
        return { ok: false, reason: 'QUOTA_EXCEEDED' };
      }
      if (err.message.includes('storage') || err.message.includes('SecurityError')) {
        return { ok: false, reason: 'STORAGE_UNAVAILABLE' };
      }
    }
    return { ok: false, reason: 'STORAGE_UNAVAILABLE' };
  }
}

export function loadSlot(storage: StorageLike, slot: SlotId): LoadResult {
  let rawJson: string | null = null;
  try {
    rawJson = storage.getItem(getSlotKey(slot));
  } catch {
    return { ok: false, reason: 'STORAGE_UNAVAILABLE' };
  }

  if (rawJson === null) {
    return { ok: false, reason: 'NO_SAVE' };
  }

  let parsed: unknown = null;
  try {
    parsed = JSON.parse(rawJson);
  } catch {
    return { ok: false, reason: 'CORRUPT_JSON' };
  }

  // 1. Validasi Envelope dasar
  const envCheck = validateEnvelope(parsed);
  if (!envCheck.ok) {
    return { ok: false, reason: envCheck.reason! };
  }

  // 2. Jalankan Migrasi jika versi < CURRENT
  const migResult = migrateEnvelope(parsed);
  if (!migResult.ok) {
    return { ok: false, reason: 'MIGRATION_FAILED', message: migResult.error };
  }

  const envelope = migResult.envelope;

  // 3. Validasi Invariant State & Konten ID
  const stateCheck = validateState(envelope.state);
  if (!stateCheck.ok) {
    return { ok: false, reason: stateCheck.reason!, message: stateCheck.message };
  }

  return {
    ok: true,
    state: envelope.state,
    savedAt: envelope.savedAt
  };
}

export function clearSlot(storage: StorageLike, slot: SlotId): void {
  try {
    storage.removeItem(getSlotKey(slot));
  } catch {
    // Ignore error
  }
}

